import { HTMLAttributes, KeyboardEvent, useState } from "react";

interface Props {
  onSend: (message: string) => void;
  disabled?: boolean;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

export default function ChatInput({
  onSend,
  disabled = false,
  className,
  onFocus,
  onBlur,
}: Props) {
  const [value, setValue] = useState("");

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className={`shrink-0 pb-[var(--chat-space-05)] ${className ?? "px-[16px] sm:px-[var(--chat-space-06)]"}`}
    >
      <div className="flex items-center gap-[var(--chat-space-03)] bg-[var(--chat-creme)] border border-[var(--chat-grey-100)] rounded-[var(--chat-corner-base)] px-[16px] md:px-[24px] min-h-[54px]">
        <textarea
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            // Auto-resize up to max height
            const el = e.target;
            el.style.height = "auto";
            el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
          }}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          placeholder="What's on your mind?"
          rows={1}
          {...({
            enterKeyHint: "send",
          } as HTMLAttributes<HTMLTextAreaElement>)}
          className="flex-1 resize-none bg-transparent text-[16px] font-medium text-[var(--chat-black)] placeholder:text-[var(--chat-black)] focus:outline-none focus:ring-0 border-none p-0 min-h-0 max-h-[120px] leading-[1.5] overflow-y-auto"
        />
        <button
          onClick={handleSend}
          disabled={disabled || !value.trim()}
          className="shrink-0 w-[var(--chat-icon-base)] h-[var(--chat-icon-base)] text-[var(--chat-black)] disabled:opacity-30"
          aria-label="Send message"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
